import { useState, useEffect, useCallback } from "react";
import { supabase, getEdgeFunctionUrl } from "@/lib/supabase";
import { useAuth } from "@/context/AuthContext";

interface AdminUser {
  id: string;
  name: string;
  email: string;
  role: string;
  created_at: string;
}

export default function AdminRoleManagement() {
  const [admins, setAdmins] = useState<AdminUser[]>([]);
  const [addEmail, setAddEmail] = useState("");
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<{ type: "success" | "error"; message: string } | null>(null);
  const { refreshProfile } = useAuth();

  const showToast = (type: "success" | "error", message: string) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 3500);
  };

  const fetchAdmins = useCallback(async () => {
    try {
      setError(null);
      const { data, error: supabaseError } = await supabase
        .from("vietnamcv_profiles")
        .select("id, name, email, role, created_at")
        .contains("roles", ["admin"])
        .order("created_at", { ascending: false });

      if (supabaseError) throw supabaseError;
      setAdmins((data || []) as AdminUser[]);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Không thể tải danh sách");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAdmins();
  }, [fetchAdmins]);

  const handleGrant = async () => {
    const email = addEmail.trim();
    if (!email) return;

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      showToast("error", "Email không đúng định dạng.");
      return;
    }

    setActionLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        showToast("error", "Phiên đăng nhập hết hạn. Vui lòng đăng nhập lại.");
        return;
      }

      const res = await fetch(getEdgeFunctionUrl("vietnamcv-admin-manage-role"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ targetEmail: email, action: "grant" }),
      });

      const data = await res.json();

      if (res.ok) {
        await refreshProfile();
        showToast("success", data.message);
        setAddEmail("");
        fetchAdmins();
      } else {
        showToast("error", data.error || "Có lỗi xảy ra");
      }
    } catch {
      showToast("error", "Lỗi kết nối. Vui lòng thử lại.");
    } finally {
      setActionLoading(false);
    }
  };

  const handleRevoke = async (email: string) => {
    if (email === "admin@vietnam.cv") {
      showToast("error", "Không thể thu hồi quyền của Super Admin.");
      return;
    }

    if (!window.confirm(`Bạn có chắc muốn thu hồi quyền admin của ${email}?`)) return;

    setActionLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        showToast("error", "Phiên đăng nhập hết hạn.");
        return;
      }

      const res = await fetch(getEdgeFunctionUrl("vietnamcv-admin-manage-role"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ targetEmail: email, action: "revoke" }),
      });

      const data = await res.json();

      if (res.ok) {
        await refreshProfile();
        showToast("success", data.message);
        fetchAdmins();
      } else {
        showToast("error", data.error || "Có lỗi xảy ra");
      }
    } catch {
      showToast("error", "Lỗi kết nối. Vui lòng thử lại.");
    } finally {
      setActionLoading(false);
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString("vi-VN", { year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" });
  };

  return (
    <div>
      {toast && (
        <div className={`fixed top-6 right-6 z-50 flex items-center gap-2 px-4 py-3 rounded-xl shadow-lg text-sm font-medium animate-fade-in-up ${
          toast.type === "success" ? "bg-green-50 text-green-800 border border-green-200" : "bg-red-50 text-red-800 border border-red-200"
        }`}>
          <i className={`${toast.type === "success" ? "ri-checkbox-circle-line" : "ri-error-warning-line"} text-base`}></i>
          {toast.message}
        </div>
      )}

      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <div>
          <h3 className="font-heading text-lg font-bold text-foreground-950">Phân quyền quản trị</h3>
          <p className="text-xs text-foreground-500 mt-0.5">
            Chỉ Super Admin (admin@vietnam.cv) mới có quyền thêm hoặc xóa quyền quản trị
          </p>
        </div>
      </div>

      {/* Grant admin form */}
      <div className="bg-background-50 border border-background-200 rounded-2xl p-5 mb-6">
        <h4 className="text-sm font-semibold text-foreground-800 mb-3">Thêm quyền quản trị</h4>
        <div className="flex items-center gap-3">
          <div className="relative flex-1">
            <i className="ri-mail-line absolute left-3.5 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
            <input
              type="email"
              value={addEmail}
              onChange={(e) => setAddEmail(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") handleGrant(); }}
              placeholder="Nhập email cần cấp quyền admin..."
              className="w-full pl-9 pr-4 py-2.5 text-sm bg-background-100 border border-background-200 rounded-xl text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
              disabled={actionLoading}
            />
          </div>
          <button
            onClick={handleGrant}
            disabled={actionLoading || !addEmail.trim()}
            className="flex items-center gap-2 px-5 py-2.5 bg-red-500 text-white rounded-full text-sm font-semibold hover:bg-red-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {actionLoading ? (
              <>
                <i className="ri-loader-4-line animate-spin text-base"></i>
                <span>Đang xử lý...</span>
              </>
            ) : (
              <>
                <i className="ri-shield-check-line text-base"></i>
                <span>Cấp quyền Admin</span>
              </>
            )}
          </button>
        </div>
      </div>

      {/* Admin list */}
      <div className="bg-background-50 border border-background-200 rounded-2xl overflow-hidden">
        {loading ? (
          <div className="p-10 space-y-4">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="flex items-center gap-4 animate-pulse">
                <div className="w-9 h-9 rounded-full bg-background-200"></div>
                <div className="flex-1 space-y-2">
                  <div className="h-4 w-1/3 bg-background-200/80 rounded"></div>
                  <div className="h-3 w-1/4 bg-background-200/50 rounded"></div>
                </div>
              </div>
            ))}
          </div>
        ) : error ? (
          <div className="flex flex-col items-center py-12 gap-3">
            <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
              <i className="ri-error-warning-line text-xl text-red-500"></i>
            </div>
            <p className="text-sm text-foreground-600">{error}</p>
            <button
              onClick={() => { setLoading(true); fetchAdmins(); }}
              className="inline-flex items-center gap-2 px-4 py-2 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-refresh-line text-sm"></i>
              <span>Thử lại</span>
            </button>
          </div>
        ) : admins.length === 0 ? (
          <div className="flex flex-col items-center py-12 gap-3">
            <div className="w-14 h-14 flex items-center justify-center bg-background-100 rounded-full">
              <i className="ri-shield-user-line text-2xl text-foreground-400"></i>
            </div>
            <p className="text-sm text-foreground-500">Chưa có tài khoản admin nào được cấp quyền</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-background-200 bg-background-100/50">
                  <th className="text-left px-5 py-3.5 text-xs font-semibold text-foreground-500 uppercase tracking-wider whitespace-nowrap">Quản trị viên</th>
                  <th className="text-left px-5 py-3.5 text-xs font-semibold text-foreground-500 uppercase tracking-wider whitespace-nowrap">Email</th>
                  <th className="text-left px-5 py-3.5 text-xs font-semibold text-foreground-500 uppercase tracking-wider whitespace-nowrap">Loại</th>
                  <th className="text-left px-5 py-3.5 text-xs font-semibold text-foreground-500 uppercase tracking-wider whitespace-nowrap">Ngày cấp quyền</th>
                  <th className="text-right px-5 py-3.5 text-xs font-semibold text-foreground-500 uppercase tracking-wider whitespace-nowrap">Thao tác</th>
                </tr>
              </thead>
              <tbody>
                {admins.map((admin) => (
                  <tr key={admin.id} className="border-b border-background-100 hover:bg-background-50 transition-colors">
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 flex items-center justify-center bg-red-50 rounded-full flex-shrink-0">
                          <i className="ri-shield-user-line text-red-500 text-base"></i>
                        </div>
                        <span className="font-semibold text-foreground-900">{admin.name}</span>
                      </div>
                    </td>
                    <td className="px-5 py-4 text-foreground-600 whitespace-nowrap text-xs">{admin.email}</td>
                    <td className="px-5 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-semibold ${
                        admin.email === "admin@vietnam.cv"
                          ? "bg-red-50 text-red-700 border border-red-200"
                          : "bg-amber-50 text-amber-700 border border-amber-200"
                      }`}>
                        {admin.email === "admin@vietnam.cv" ? "Super Admin" : "Admin"}
                      </span>
                    </td>
                    <td className="px-5 py-4 text-foreground-500 whitespace-nowrap text-xs">{formatDate(admin.created_at)}</td>
                    <td className="px-5 py-4">
                      <div className="flex items-center justify-end">
                        {admin.email !== "admin@vietnam.cv" && (
                          <button
                            onClick={() => handleRevoke(admin.email)}
                            disabled={actionLoading}
                            className="flex items-center gap-1.5 px-3 py-1.5 bg-red-50 text-red-600 rounded-lg text-xs font-semibold hover:bg-red-100 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
                          >
                            <i className="ri-shield-cross-line text-sm"></i>
                            <span>Thu hồi quyền</span>
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="flex items-center justify-between mt-4 px-2">
        <p className="text-xs text-foreground-500">
          Tổng cộng <strong>{admins.length}</strong> tài khoản quản trị
        </p>
      </div>
    </div>
  );
}